/**
 * Helpers for the taxonomy rule model. A rule is a decision table of
 * `branches` (IF-group → THEN); legacy rules stored a single flat AND list.
 * `getRuleBranches` normalizes both into branches so every consumer — the
 * editor, the list display, the version diff — reads one shape.
 */
import { ruleBranchesOf } from '@inference.engine/taxonomy/web';
import { i18n } from '@/shared/i18n';
import type { TaxonomyRule, TaxonomyRuleBranch, TaxonomyRuleConditionGroup } from '../model/types';

/** An empty AND group (the starting point for a new branch's condition tree). */
export function emptyConditionGroup(): TaxonomyRuleConditionGroup {
  return { combinator: 'and', conditions: [], groups: [] };
}

/** Normalizes any rule (legacy-flat or multi-branch) into its list of branches. */
export function getRuleBranches(rule: TaxonomyRule): TaxonomyRuleBranch[] {
  return ruleBranchesOf(rule);
}

/** Total number of leaf conditions across a group and all its descendants. */
export function countConditions(group: TaxonomyRuleConditionGroup): number {
  return group.conditions.length + group.groups.reduce((n, g) => n + countConditions(g), 0);
}

// Getters, not values: a resolved label here would freeze at import.
const OPERATOR_LABEL: Record<string, string> = {
  get is() {
    return i18n.t('knowledge:taxonomy.rules.is');
  },
  get is_not() {
    return i18n.t('knowledge:taxonomy.rules.isNot');
  },
};

// A character no field name or value typed into the builder holds.
const STAND_IN_MARK = String.fromCharCode(0);

/**
 * Translates with stand-ins and puts `values` in afterwards. i18next fills placeholders one after
 * another, so a field name or value holding "{{then}}" would otherwise be filled by a later one.
 */
export function withLiteralValues<K extends string>(
  translate: (standIns: Record<K, string>) => string,
  values: Record<K, string>,
): string {
  const keys = Object.keys(values) as K[];
  const standIns = Object.fromEntries(
    keys.map((key, index) => [key, STAND_IN_MARK + String(index) + STAND_IN_MARK]),
  ) as Record<K, string>;
  let text = translate(standIns);
  for (const key of keys) text = text.replaceAll(standIns[key], () => values[key]);
  return text;
}

/**
 * Renders a condition group to a readable boolean expression, e.g.
 * `Region is “Regional” AND (Country is “US” OR Country is “CA”)`. Nested
 * groups are parenthesized; the top-level call omits the outer parens.
 */
export function describeConditionGroup(
  group: TaxonomyRuleConditionGroup,
  nameOf: (id: string) => string,
  depth = 0,
): string {
  const parts: string[] = [
    ...group.conditions.map((c) =>
      withLiteralValues((standIns) => i18n.t('knowledge:taxonomy.rules.condition', standIns), {
        field: nameOf(c.fieldId),
        operator: OPERATOR_LABEL[c.operator] ?? OPERATOR_LABEL.is,
        value: c.value,
      }),
    ),
    ...group.groups.map((g) => describeConditionGroup(g, nameOf, depth + 1)),
  ];
  if (parts.length === 0) return i18n.t('knowledge:taxonomy.rules.noConditions');
  const joined = parts.join(
    group.combinator === 'or'
      ? i18n.t('knowledge:taxonomy.rules.orJoiner')
      : i18n.t('knowledge:taxonomy.rules.andJoiner'),
  );
  return depth > 0 && parts.length > 1 ? `(${joined})` : joined;
}

/** Human summary of one branch's THEN action. */
export function describeBranchAction(
  branch: TaxonomyRuleBranch,
  nameOf: (id: string) => string,
): string {
  const target = nameOf(branch.targetFieldId);
  switch (branch.action) {
    case 'limit_options':
      return withLiteralValues(
        (standIns) => i18n.t('knowledge:taxonomy.rules.limitOptions', standIns),
        { target, values: (branch.values ?? []).join(', ') || '—' },
      );
    case 'show_field':
      return i18n.t('knowledge:taxonomy.rules.showField', { target });
    case 'set_default':
      return withLiteralValues(
        (standIns) => i18n.t('knowledge:taxonomy.rules.setDefault', standIns),
        { target, value: branch.defaultValue || '—' },
      );
    case 'require_field':
      return i18n.t('knowledge:taxonomy.rules.requireField', { target });
    default:
      return target;
  }
}
